import axios from "axios";
import appConfig from "config";
import { store } from "index";

let isRefreshing = false;
let isTokenRefreshPending: Promise<void> | null = null;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const axiosInstance = axios.create({
  baseURL: appConfig.api.API,
  withCredentials: true,
});

axiosInstance.interceptors.request.use(
  async (config) => {
    if (isTokenRefreshPending) {
      try {
        await isTokenRefreshPending;
      } catch (err) {
        return Promise.reject(err);
      }
    }

    const authUser = sessionStorage.getItem("authUser");
    const token = authUser ? JSON.parse(authUser).token : null;

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    const state = store.getState();
    config.headers["Content-Type"] = "application/json";
    // console.log("config headers....", config.headers);
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    
    // Check for both admin and user refresh token endpoints
    const isRefreshUrl = originalRequest.url?.includes("/adminauth/refreshtoken") || 
                        originalRequest.url?.includes("/user/refreshtoken");
    
    const authUser = sessionStorage.getItem("authUser");
    const refresh_token = authUser ? JSON.parse(authUser).refresh_token : null;
    
    // Handle 401 Unauthorized - Token expired
    if (status === 401 && !originalRequest._retry && !isRefreshUrl && refresh_token) {
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      isTokenRefreshPending = new Promise(async (resolve, reject) => {
        try {
          console.log("=== TOKEN REFRESH ATTEMPT ===");
          console.log("Original Request URL:", originalRequest.url);
          console.log("Refresh Token Available:", !!refresh_token);
          
          // Try user refresh token endpoint first, fallback to admin
          let refreshEndpoint = "/user/refreshtoken";
          let refreshResponse;
          
          try {
            // Use axios directly to avoid interceptor recursion (refresh calls should not trigger refresh)
            // But we need to use the baseURL from config
            const baseURL = appConfig.api.API;
            refreshResponse = await axios.post(
              `${baseURL}${refreshEndpoint}`,
              { refresh_token },
              {
                withCredentials: true,
                headers: {
                  "Content-Type": "application/json",
                }
              }
            );
            console.log("Refresh token API called successfully via /user/refreshtoken");
          } catch (userRefreshError: any) {
            // If user endpoint fails, try admin endpoint
            if (userRefreshError?.response?.status === 404 || userRefreshError?.response?.status === 400) {
              console.log("User refresh endpoint failed, trying admin endpoint...");
              refreshEndpoint = "/adminauth/refreshtoken";
              const baseURL = appConfig.api.API;
              refreshResponse = await axios.post(
                `${baseURL}${refreshEndpoint}`,
                { refresh_token },
                {
                  withCredentials: true,
                  headers: {
                    "Content-Type": "application/json",
                  }
                }
              );
              console.log("Refresh token API called successfully via /adminauth/refreshtoken");
            } else {
              throw userRefreshError;
            }
          }

          console.log("=== REFRESH TOKEN RESPONSE ===");
          console.log("Status:", refreshResponse.status);
          console.log("Response Data:", JSON.stringify(refreshResponse.data, null, 2));
          
          // Handle different response structures
          let newAccessToken = null;
          let newRefreshToken = null;
          
          if (refreshResponse.data?.data) {
            // Admin API structure: { data: "token_string" }
            newAccessToken = refreshResponse.data.data;
          } else if (refreshResponse.data?.token) {
            // User API structure: { token: "...", refresh_token: "..." }
            newAccessToken = refreshResponse.data.token;
            newRefreshToken = refreshResponse.data.refresh_token;
          } else if (refreshResponse.data?.success && refreshResponse.data?.data?.token) {
            // Wrapped structure: { success: true, data: { token: "...", refresh_token: "..." } }
            newAccessToken = refreshResponse.data.data.token;
            newRefreshToken = refreshResponse.data.data.refresh_token;
          }
          
          if (!newAccessToken) {
            throw new Error("No access token returned from refresh endpoint");
          }

          // Update sessionStorage with new tokens
          const authUser = sessionStorage.getItem("authUser");
          if (authUser) {
            const parsed = JSON.parse(authUser);
            parsed.token = newAccessToken;
            if (newRefreshToken) {
              parsed.refresh_token = newRefreshToken;
            }
            sessionStorage.setItem("authUser", JSON.stringify(parsed));
            console.log("Tokens updated in sessionStorage");
          }

          // Update axios default headers
          axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`;
          
          // Process queued requests
          processQueue(null, newAccessToken);
          
          // Update original request header
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
          
          console.log("Token refreshed successfully");
          console.log("=============================");
          
          resolve();
        } catch (err: any) {
          console.log("=== TOKEN REFRESH FAILED ===");
          console.log("Error:", err);
          console.log("Error Status:", err?.response?.status);
          console.log("Error Data:", err?.response?.data);
          console.log("============================");
          
          processQueue(err, null);

          const axiosError = err as any;
          const refreshStatus = axiosError?.response?.status;

          const isRefreshCall = originalRequest.url?.includes("/adminauth/refreshtoken") ||
                               originalRequest.url?.includes("/user/refreshtoken");

          // If refresh token is invalid or expired, logout user
          if (isRefreshCall || [401, 403, 404, 500, 400].includes(refreshStatus)) {
            console.warn("Refresh token failed or expired. Logging out user.");
            sessionStorage.removeItem("authUser");
            sessionStorage.removeItem("isLoggedIn");
            window.location.href = "/login";
            reject(err);
            return;
          }

          reject(err);
        } finally {
          isRefreshing = false;
          isTokenRefreshPending = null;
        }
      });

      try {
        await isTokenRefreshPending;
        return axiosInstance(originalRequest);
      } catch (err) {
        return Promise.reject(err);
      }
    }

    // Still failing after refresh attempt — force logout
    if (status === 401 && originalRequest._retry) {
      console.warn("Request failed after token refresh. Logging out user.");
      sessionStorage.removeItem("authUser");
      sessionStorage.removeItem("isLoggedIn");
      window.location.href = "/login";
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;

